import type { ColumnDef } from '@tanstack/react-table'
import { Clock } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { CompactTable, EmptyTableBody, SectionTitle, Stat, Stats } from 'shared/components/section'
import { ToneNote } from 'shared/components/tone-banner'
import { Alert, AlertTitle } from 'shared/components/ui/alert'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from 'shared/components/ui/dialog'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { cn } from 'shared/lib/utils'
import { formatDayTime } from 'shared/lib/utils/date'
import { MONEY_COLUMN, som, sum, WHOLE } from 'shared/lib/money'

export type CashBagPreparation = RouterOutputs['cashBags']['Prepare']
export type CashBagPackRequest = RouterInputs['cashBags']['Pack']
export type CashBagPackAnswer = RouterOutputs['cashBags']['Pack']
type PackRefusal = Extract<CashBagPackAnswer, { outcome: 'refused' }>['reason']
type Cover = CashBagPreparation['covers'][number]

const prepareViaTrpc = () => trpcClient.cashBags.Prepare.query({})
const packViaTrpc = (input: CashBagPackRequest) => trpcClient.cashBags.Pack.mutate(input)

const failure = (error: unknown) => (error instanceof Error ? error.message : String(error))

/**
 * The Cash bag at the Point (ADR-0036, ADR-0047; tickets 30, 57; user story 97): the
 * bag takes the **whole** Cashbox — every partner's cash. The page shows the amount
 * per partner and the counter payments since the last bag that it covers (a
 * cancellation is a minus); «Упаковать сумку» opens a dialog where the operator
 * enters the amount counted and packs the bag in front of the driver, writing the
 * Point and the date on it — there is no seal. A count that differs shows its
 * difference at once — it is recorded against the operator — and needs a comment.
 * One bag waits per Point; the bag is never opened at the Point.
 *
 * Enter moves on like Tab (spec §26): the count → the comment → «Упаковать сумку»,
 * and a second Enter there confirms. The server decides; a refusal is named by its
 * reason.
 *
 * `prepare` and `pack` are injected so the behaviour is tested without a backend.
 * `packs` — whether this person packs (the operator); the admin only reads.
 * `notice` sits above the figures (no open Shift, in the app); `toolbar` is the row
 * the main button goes in.
 */
export function CashBagCard({
	prepare = prepareViaTrpc,
	pack = packViaTrpc,
	onPacked,
	packs = true,
	notice,
	toolbar = (action) => action && <div className='flex justify-end'>{action}</div>,
}: {
	prepare?: () => Promise<CashBagPreparation>
	pack?: (input: CashBagPackRequest) => Promise<CashBagPackAnswer>
	onPacked?: () => void
	packs?: boolean
	notice?: ReactNode
	toolbar?: (action: ReactNode) => ReactNode
}) {
	const { t } = useTranslate()
	const [shown, setShown] = useState<CashBagPreparation | null>(null)
	const [loadError, setLoadError] = useState<string | null>(null)
	const [packing, setPacking] = useState(false)
	const [packed, setPacked] = useState<string | null>(null)

	const refresh = useCallback(() => {
		prepare().then(
			(next) => {
				setShown(next)
				setLoadError(null)
			},
			(e) => setLoadError(failure(e)),
		)
	}, [prepare])

	useEffect(() => {
		refresh()
	}, [refresh])

	const waiting = shown?.waiting[0] ?? null
	const canPack = packs && !!shown && shown.cash > 0 && !waiting

	const action = canPack ? (
		<Button
			data-testid='cash-bag-pack'
			onClick={() => {
				setPacked(null)
				setPacking(true)
			}}
		>
			{t('cashBag.submit')}
		</Button>
	) : null

	return (
		<div className='flex flex-col gap-6' data-testid='cash-bag-card'>
			{toolbar(action)}
			{notice}

			{packed && <ToneNote tone='ready' title={t('cashBag.packedTitle')} detail={packed} testId='cash-bag-message' />}
			{waiting && (
				<Alert data-testid='cash-bag-waiting'>
					<Clock />
					<AlertTitle>
						{t('cashBag.waiting', { point: waiting.pointCode, amount: som(t, waiting.countedCash) })}
					</AlertTitle>
				</Alert>
			)}

			{loadError ? (
				<p role='alert' className='text-sm text-destructive' data-testid='cash-bag-load-error'>
					{t('cashBag.failed', { message: loadError })}
				</p>
			) : !shown ? (
				<p className='text-sm text-muted-foreground'>{t('money.loading')}</p>
			) : (
				<>
					<section className='flex flex-col gap-3'>
						<SectionTitle title={t('cashBag.cashboxTitle')} hint={t('cashBag.explain')} />
						<Stats>
							<Stat label={t('cashBag.takes')} value={sum(shown.cash)} valueTestId='cash-bag-cash' />
						</Stats>
						<Partners shown={shown} />
					</section>

					<section className='flex flex-col gap-2'>
						<SectionTitle title={t('cashBag.covers')} count={shown.covers.length} />
						<Covers covers={shown.covers} />
					</section>
				</>
			)}

			<Dialog open={packing} onOpenChange={setPacking}>
				{shown && packing && (
					<PackDialog
						shown={shown}
						pack={pack}
						onCancel={() => setPacking(false)}
						onPacked={(text) => {
							setPacking(false)
							setPacked(text)
							onPacked?.()
							refresh()
						}}
						onStale={refresh}
					/>
				)}
			</Dialog>
		</div>
	)
}

/** «Упаковать сумку»: the amount counted, a comment if it differs, the Point and the date on the bag. */
function PackDialog({
	shown,
	pack,
	onCancel,
	onPacked,
	onStale,
}: {
	shown: CashBagPreparation
	pack: (input: CashBagPackRequest) => Promise<CashBagPackAnswer>
	onCancel: () => void
	onPacked: (text: string) => void
	/** The server read other money than the one shown: read it again. */
	onStale: () => void
}) {
	const { t } = useTranslate()
	const [counted, setCounted] = useState('')
	const [comment, setComment] = useState('')
	const [sending, setSending] = useState(false)
	const [message, setMessage] = useState<string | null>(null)
	const commentRef = useRef<HTMLInputElement>(null)
	const submitRef = useRef<HTMLButtonElement>(null)
	// A count that differs is explained: the cursor goes to the comment once the form is live again.
	const [askComment, setAskComment] = useState(0)
	useEffect(() => {
		if (askComment > 0 && !sending) commentRef.current?.focus()
	}, [askComment, sending])

	const countedCash = counted.trim() === '' ? null : Number(counted)
	const difference = countedCash !== null && Number.isFinite(countedCash) ? countedCash - shown.cash : 0
	const ready = countedCash !== null && countedCash >= 0

	const submit = async () => {
		if (!ready || sending || countedCash === null) return
		setSending(true)
		setMessage(null)
		try {
			const res = await pack({
				asOf: shown.asOf,
				countedCash,
				...(comment.trim() ? { comment: comment.trim() } : {}),
			})
			if (res.outcome === 'packed') {
				onPacked(
					t('cashBag.packed', { point: res.bag.pointCode, amount: som(t, res.bag.countedCash) }),
				)
				return
			}
			setMessage(t(`cashBagRefusal.${res.reason satisfies PackRefusal}`))
			if (res.reason === 'cash_bag_comment_required') setAskComment((n) => n + 1)
			if (res.reason === 'cashbox_changed' || res.reason === 'cash_bag_waiting') onStale()
		} catch (e) {
			setMessage(t('cashBag.failed', { message: failure(e) }))
		} finally {
			setSending(false)
		}
	}

	return (
		<DialogContent className='sm:max-w-md' data-testid='cash-bag-form'>
			<DialogHeader>
				<DialogTitle>{t('cashBag.submit')}</DialogTitle>
				<DialogDescription data-testid='cash-bag-write-on-bag'>
					{t('cashBag.writeOnBag', { point: shown.pointCode })}
				</DialogDescription>
			</DialogHeader>
			<div className='flex flex-col gap-4'>
				<Stats>
					<Stat label={t('cashBag.takes')} value={sum(shown.cash)} />
				</Stats>
				<div className='flex flex-col gap-1.5'>
					<Label htmlFor='cash-bag-counted'>{t('cashBag.counted')}</Label>
					<Input
						id='cash-bag-counted'
						data-testid='cash-bag-counted'
						type='number'
						min={0}
						inputMode='numeric'
						autoComplete='off'
						autoFocus
						value={counted}
						onChange={(e) => setCounted(e.target.value)}
						onKeyDown={(e) => {
							if (e.key !== 'Enter') return
							e.preventDefault()
							commentRef.current?.focus()
						}}
					/>
					{difference !== 0 && (
						<p className='text-sm font-medium text-destructive' data-testid='cash-bag-difference'>
							{t(difference < 0 ? 'cashBag.shortage' : 'cashBag.surplus', {
								amount: som(t, Math.abs(difference)),
							})}
						</p>
					)}
				</div>
				<div className='flex flex-col gap-1.5'>
					<Label htmlFor='cash-bag-comment'>{t('cashBag.comment')}</Label>
					<Input
						id='cash-bag-comment'
						ref={commentRef}
						data-testid='cash-bag-comment'
						autoComplete='off'
						value={comment}
						placeholder={t('cashBag.commentPlaceholder')}
						onChange={(e) => setComment(e.target.value)}
						onKeyDown={(e) => {
							if (e.key !== 'Enter') return
							e.preventDefault()
							submitRef.current?.focus()
						}}
					/>
				</div>
				{message !== null && (
					<p role='alert' className='text-sm text-destructive' data-testid='cash-bag-message' data-tone='error'>
						{message}
					</p>
				)}
			</div>
			<DialogFooter>
				<Button type='button' variant='outline' disabled={sending} onClick={onCancel}>
					{t('cashBag.cancel')}
				</Button>
				<Button ref={submitRef} type='button' data-testid='cash-bag-submit' disabled={!ready || sending} onClick={submit}>
					{t('cashBag.submit')}
				</Button>
			</DialogFooter>
		</DialogContent>
	)
}

/** What the bag takes, per partner. */
function Partners({ shown }: { shown: CashBagPreparation }) {
	const { t } = useTranslate()
	// A partner whose share is 0 (all of it went in the last bag) is not money held: an empty Cashbox says so in one row.
	const held = shown.partners.filter((p) => p.cash !== 0)
	return (
		<CompactTable testId='cash-bag-partners'>
			<TableHeader>
				<TableRow>
					<TableHead>{t('money.colPartner')}</TableHead>
					<TableHead className='text-right'>{t('money.colAmount')}</TableHead>
				</TableRow>
			</TableHeader>
			{held.length === 0 ? (
				<EmptyTableBody columns={2}>
					<span data-testid='cash-bag-empty'>{t('cashBag.empty')}</span>
				</EmptyTableBody>
			) : (
				<TableBody>
					{held.map((p) => (
						<TableRow key={p.partnerId ?? 'none'} data-testid={`cash-bag-partner-${p.partnerId ?? 'none'}`}>
							<TableCell>{p.partnerName || t('money.noPartner')}</TableCell>
							<TableCell className='text-right tabular-nums'>{sum(p.cash)}</TableCell>
						</TableRow>
					))}
				</TableBody>
			)}
		</CompactTable>
	)
}

/** The counter payments since the last bag that this one covers; a cancellation is a minus. */
function Covers({ covers }: { covers: Cover[] }) {
	const { t } = useTranslate()
	const columns = useMemo<ColumnDef<Cover>[]>(
		() => [
			{
				id: 'parcel',
				header: t('cashBag.colParcel'),
				cell: ({ row }) => <span className='font-mono'>{row.original.trackingCode}</span>,
			},
			{
				id: 'partner',
				header: t('money.colPartner'),
				cell: ({ row }) => row.original.partnerName || t('money.noPartner'),
			},
			{
				id: 'kind',
				header: t('cashBag.colKind'),
				cell: ({ row }) => (
					<Badge variant='outline' className='font-normal'>
						{t(row.original.kind === 'collected' ? 'cashBag.kindCollected' : 'cashBag.kindCancelled')}
					</Badge>
				),
			},
			{
				id: 'at',
				header: t('money.colAt'),
				cell: ({ row }) => <span className='tabular-nums'>{formatDayTime(row.original.at)}</span>,
			},
			{
				id: 'amount',
				header: t('money.colAmount'),
				meta: MONEY_COLUMN,
				cell: ({ row }) => (
					<span className={cn(row.original.amount < 0 && 'text-muted-foreground')}>{sum(row.original.amount)}</span>
				),
			},
		],
		[t],
	)
	const { table } = useDataTable({
		data: covers,
		columns,
		rowCount: covers.length,
		...WHOLE,
		getRowId: (c) => c.eventId,
	})
	return (
		<div data-testid='cash-bag-covers'>
			<DataTable
				table={table}
				withPagination={false}
				emptyText={<span data-testid='cash-bag-no-covers'>{t('cashBag.noCovers')}</span>}
				row={{ attributes: (c) => ({ 'data-testid': `cash-bag-cover-${c.eventId}` }) }}
			/>
		</div>
	)
}
